import { copyToClipboard } from '@xver/utils/clipboard';
import { IconCopy, IconTrash } from '@tabler/icons-react';
import type { SSHKeyResponseModel } from '@xver/apis/v1/ssh-keys/model';
import { Button } from '@xver/components/Atoms';
import { DataTable, DataTableColumn } from '@xver/components/Organisms';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface SSHKeyListTableProps {
    rows: SSHKeyResponseModel[] | undefined;
    isLoading?: boolean;
    onDelete: (key: SSHKeyResponseModel) => void;
}

export default function SSHKeyListTable({ rows, isLoading, onDelete }: SSHKeyListTableProps) {
    const { t } = useTranslation('sshKeys');

    const columns = useMemo<DataTableColumn<SSHKeyResponseModel>[]>(
        () => [
            {
                key: 'name',
                label: t('table.columns.name'),
                render: (row) => (
                    <div className="flex flex-col">
                        <span className="font-medium">{row.name}</span>
                        <span className="text-xs text-fg/68">{row.type}</span>
                    </div>
                ),
            },
            {
                key: 'fingerprint',
                label: t('table.columns.fingerprint'),
                render: (row) => (
                    <span className="font-mono text-xs text-fg/70 break-all">
                        {row.fingerprint}
                    </span>
                ),
            },
            {
                key: 'copy',
                label: <span className="sr-only">{t('common:actions.copy')}</span>,
                align: 'right',
                render: (row) => (
                    <button
                        type="button"
                        aria-label={t('table.copy', { name: row.name })}
                        onClick={() => copyToClipboard(row.content)}
                        className="inline-flex items-center justify-center px-2 py-1 rounded text-fg/68 hover:text-fg"
                    >
                        <IconCopy size={16} aria-hidden="true" />
                    </button>
                ),
            },
        ],
        [t]
    );

    return (
        <DataTable
            caption={t('table.title')}
            columns={columns}
            rows={rows}
            isLoading={isLoading}
            emptyMessage={t('table.empty')}
            rowKey={(row) => row.id}
            actions={(row) => (
                <Button
                    size="sm"
                    variant="danger"
                    leftIcon={<IconTrash size={14} />}
                    onClick={() => onDelete(row)}
                >
                    {t('table.delete')}
                </Button>
            )}
        />
    );
}
